import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { safeHtml } from "../lib/sanitize";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterStrip from "../components/NewsletterStrip";
import { fmtDuration } from "./VideosPage";

function toEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function VideoDetailPage() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/videos/slug/${slug}`);
        setV(data);
        const rel = await api.get(`/videos?status=published&is_vlog=true&limit=4`);
        setRelated(rel.data.items.filter((x) => x.slug !== slug).slice(0, 3));
      } catch (err) {
        console.error("Failed to load video:", err);
      }
    })();
  }, [slug]);

  if (!v) return (<div className="min-h-screen"><Header /><div className="container-page py-24 font-mono text-sm text-[#4A4742]">Loading…</div></div>);

  const embed = toEmbedUrl(v.video_url);

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Header />

      <section className="bg-[#1C1A17] text-[#F9F9F6]" data-testid="video-detail-hero">
        <div className="container-page py-10">
          <Link to="/videos" className="overline text-[#D6CFC4] hover:text-white">← All episodes</Link>
          <div className="overline text-[#B4533C] mt-6">
            {v.season ? `S${v.season} · ` : ""}{v.episode_number ? `Episode ${v.episode_number}` : "Episode"}
            {v.location && ` · ${v.location}`}
          </div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.03] mt-3" data-testid="video-title">{v.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-mono text-[#D6CFC4]">
            {v.host && <span>Hosted by {v.host}</span>}
            {v.guests?.length > 0 && <><span>·</span><span>with {v.guests.join(", ")}</span></>}
            {v.duration_seconds && <><span>·</span><span>{fmtDuration(v.duration_seconds)}</span></>}
            {v.published_at && <><span>·</span><span>{new Date(v.published_at).toLocaleDateString()}</span></>}
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="aspect-video bg-black border border-[#D6CFC4]" data-testid="video-player">
          {embed ? (
            <iframe
              src={embed}
              className="w-full h-full"
              title={v.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            v.thumbnail ? (
              <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-white font-mono text-xs">No video source yet</div>
            )
          )}
        </div>
      </section>

      <article className="container-page pb-14 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {v.description && (
            <section data-testid="video-description">
              <div className="overline text-[#B4533C] mb-2">Show notes</div>
              <div className="prose-editorial" dangerouslySetInnerHTML={safeHtml(v.description)} />
            </section>
          )}

          {v.transcript && (
            <section className="mt-12">
              <div className="overline text-[#B4533C] mb-2">Transcript</div>
              <h2 className="font-serif text-2xl mb-4">Full transcript</h2>
              <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#1C1A17] bg-white border border-[#D6CFC4] p-5" data-testid="video-transcript">{v.transcript}</pre>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-6">
          {v.chapters?.length > 0 && (
            <div className="border border-[#D6CFC4] bg-white p-5" data-testid="video-chapters">
              <div className="overline text-[#B4533C] mb-3">Chapters</div>
              <ul className="space-y-2 text-sm">
                {v.chapters.map((c) => (
                  <li key={`${c.t}-${c.title}`} className="flex gap-3">
                    <span className="font-mono text-xs text-[#4A4742] w-12 shrink-0">{c.t}</span>
                    <span>{c.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {v.tags?.length > 0 && (
            <div className="border border-[#D6CFC4] bg-white p-5">
              <div className="overline text-[#B4533C] mb-3">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {v.tags.map((t) => (
                  <span key={t} className="text-xs font-mono uppercase tracking-wider border border-[#D6CFC4] px-2 py-1">{t}</span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </article>

      {related.length > 0 && (
        <section className="container-page py-14 border-t border-[#D6CFC4]">
          <div className="overline text-[#B4533C]">Keep watching</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-8">More episodes</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((r) => (
              <Link to={`/video/${r.slug}`} key={r.id} className="group block">
                <div className="img-zoom aspect-video overflow-hidden bg-[#EFECE5] border border-[#D6CFC4]">
                  {r.thumbnail && <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="overline text-[#B4533C] mt-3">EP {r.episode_number || "—"}</div>
                <div className="font-serif text-lg leading-tight mt-1 group-hover:text-[#B4533C]">{r.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <NewsletterStrip />
      <Footer />
    </div>
  );
}
